import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ExternalLink,
  PackageX,
  ScanLine,
  ShieldAlert,
  TrendingDown,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { KPICard } from '@/components/charts/KPICard';
import { BarChart } from '@/components/charts/BarChart';
import { LineChart } from '@/components/charts/LineChart';
import { DonutChart } from '@/components/charts/DonutChart';
import { palette } from '@/components/charts/chartTheme';
import { useFilters, rangeISO } from '@/state/filters';
import {
  useShrinkageAnomalies,
  useShrinkageAttribution,
  useShrinkageHotspots,
  useShrinkageOverview,
  useShrinkageVariance,
} from '@/api/bi';
import type {
  ShrinkageAnomaly,
  ShrinkageAttributionUser,
  ShrinkageMatrixCell,
  ShrinkagePersistentRow,
  ShrinkageSkuRow,
} from '@/api/bi';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const REASON_LABELS: Record<string, string> = {
  theft: 'Theft',
  misplaced: 'Misplaced',
  expired: 'Expired',
  damaged: 'Damaged',
  unknown: 'Unknown',
};

const SOURCE_LABELS: Record<string, string> = {
  support_call: 'Support call',
  dayclose_variance: 'Day-close variance',
  audit: 'Audit',
  inventory_count: 'Inventory count',
};

function pctText(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toFixed(2)}%`;
}

/**
 * Shrinkage — darkstore stock-loss analytics.
 *
 * Read-only surface over existing loss records (CRM shrinkage incidents,
 * lost items, damage, physical counts, ledger). Incidents themselves are
 * managed in the CRM workflow at /crm/shrinkage — this page only analyses.
 */
export function ShrinkagePage() {
  const { range, storeId, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const filters = { start: iso.start, end: iso.end, storeId };

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Shrinkage"
        description="Detect and minimize darkstore stock loss — theft, misplacement, damage, and unexplained variance."
        actions={
          <Link
            to="/crm/shrinkage"
            className="press inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted focus-ring"
          >
            Manage incidents
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Link>
        }
      />

      <Tabs defaultValue="overview" className="mt-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="hotspots">Hotspots</TabsTrigger>
          <TabsTrigger value="variance">Count vs Book</TabsTrigger>
          <TabsTrigger value="attribution">Attribution</TabsTrigger>
          <TabsTrigger value="anomalies">Anomalies</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab filters={filters} />
        </TabsContent>
        <TabsContent value="hotspots" className="mt-4">
          <HotspotsTab filters={filters} />
        </TabsContent>
        <TabsContent value="variance" className="mt-4">
          <VarianceTab filters={filters} />
        </TabsContent>
        <TabsContent value="attribution" className="mt-4">
          <AttributionTab filters={filters} />
        </TabsContent>
        <TabsContent value="anomalies" className="mt-4">
          <AnomaliesTab filters={filters} />
        </TabsContent>
      </Tabs>
    </>
  );
}

interface TabProps {
  filters: { start: string; end: string; storeId: number | null };
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewTab({ filters }: TabProps) {
  const q = useShrinkageOverview(filters);
  const d = q.data;

  const reasonDonut = useMemo(
    () =>
      (d?.by_reason ?? []).map((r) => ({
        name: REASON_LABELS[r.reason] ?? r.reason,
        value: r.value,
      })),
    [d],
  );
  const trend = useMemo(
    () => (d?.trend ?? []).map((t) => ({ date: t.date ?? '', value: t.value, units: t.units })),
    [d],
  );

  if (q.isError) return <ErrorState onRetry={() => void q.refetch()} />;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Shrinkage value"
          value={fmt.npr(d?.kpis.total_value ?? 0)}
          loading={q.isLoading}
          hint="Approved shrinkage incident value in window"
        />
        <KPICard
          label="Shrinkage rate"
          value={pctText(d?.kpis.shrinkage_rate_pct)}
          loading={q.isLoading}
          hint="Approved loss value ÷ COGS (ledger sales)"
        />
        <KPICard
          label="Units lost"
          value={fmt.number(d?.kpis.total_units ?? 0)}
          loading={q.isLoading}
          hint={d ? `${fmt.number(d.kpis.lines)} incident lines` : undefined}
        />
        <KPICard
          label="Unknown / theft share"
          value={pctText(d?.kpis.unknown_share_pct)}
          loading={q.isLoading}
          hint={d ? `${fmt.npr(d.kpis.unknown_value)} unexplained` : undefined}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Shrinkage value trend</CardTitle>
          </CardHeader>
          <CardBody>
            {q.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : trend.length === 0 ? (
              <EmptyState
                icon={<TrendingDown className="h-5 w-5" />}
                title="No approved shrinkage"
                description="No approved shrinkage incidents in this window."
              />
            ) : (
              <LineChart
                data={trend}
                xKey="date"
                series={[{ key: 'value', label: 'Loss value', color: palette.series[0]() }]}
                formatValue={(v) => fmt.npr(v)}
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By reason</CardTitle>
          </CardHeader>
          <CardBody>
            {q.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : reasonDonut.length === 0 ? (
              <EmptyState icon={<PackageX className="h-5 w-5" />} title="No data" />
            ) : (
              <>
                <DonutChart data={reasonDonut} formatValue={(v) => fmt.npr(v)} />
                <ul className="mt-3 space-y-1 text-sm">
                  {(d?.by_reason ?? []).map((r, i) => (
                    <li key={r.reason} className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: palette.series[i % palette.series.length]() }}
                        />
                        {REASON_LABELS[r.reason] ?? r.reason}
                      </span>
                      <span className="text-muted-foreground">
                        {fmt.npr(r.value)} · {fmt.number(r.units)}u
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Leakage funnel</CardTitle>
          </CardHeader>
          <CardBody>
            {q.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : !d ? null : (
              <ul className="space-y-2 text-sm">
                <FunnelRow label="Lost-item reports" value={d.funnel.lost_items_reported} />
                <FunnelRow label="Incidents raised" value={d.funnel.incidents_total} />
                <FunnelRow label="Verified" value={d.funnel.incidents_verified} />
                <FunnelRow label="Approved" value={d.funnel.incidents_approved} tone="danger" />
                <FunnelRow label="Rejected" value={d.funnel.incidents_rejected} />
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Discovery source</CardTitle>
          </CardHeader>
          <CardBody>
            {q.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (d?.by_discovery_source ?? []).length === 0 ? (
              <EmptyState icon={<ScanLine className="h-5 w-5" />} title="No incidents" />
            ) : (
              <ul className="space-y-2 text-sm">
                {(d?.by_discovery_source ?? []).map((s) => (
                  <li
                    key={s.discovery_source}
                    className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
                  >
                    <span>{SOURCE_LABELS[s.discovery_source] ?? s.discovery_source}</span>
                    <span className="text-muted-foreground">
                      {fmt.number(s.incidents)} · {fmt.npr(s.value)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function FunnelRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'danger';
}) {
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('kpi-num font-semibold', tone === 'danger' && 'text-danger')}>
        {fmt.number(value)}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Hotspots
// ---------------------------------------------------------------------------

function HotspotsTab({ filters }: TabProps) {
  const q = useShrinkageHotspots(filters);
  const d = q.data;

  const topSubcats = useMemo(() => {
    const byCat = new Map<string, number>();
    for (const cell of d?.matrix ?? []) {
      byCat.set(cell.subcategory_name, (byCat.get(cell.subcategory_name) ?? 0) + cell.value);
    }
    return Array.from(byCat.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  }, [d]);

  if (q.isError) return <ErrorState onRetry={() => void q.refetch()} />;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Loss value by subcategory</CardTitle>
        </CardHeader>
        <CardBody>
          {q.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : topSubcats.length === 0 ? (
            <EmptyState
              icon={<PackageX className="h-5 w-5" />}
              title="No shrinkage hotspots"
              description="No approved shrinkage by subcategory in this window."
            />
          ) : (
            <BarChart
              data={topSubcats}
              xKey="label"
              yKey="value"
              horizontal
              height={Math.max(220, topSubcats.length * 30)}
              formatValue={(v) => fmt.npr(v)}
              colorFor={(_, i) => palette.series[i % palette.series.length]()}
            />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SKU outliers</CardTitle>
          <Badge tone="neutral">value ≥ 2× subcategory mean</Badge>
        </CardHeader>
        <CardBody className="!p-0">
          <ShrinkageSkuTable rows={d?.top_skus ?? []} loading={q.isLoading} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Store × subcategory matrix</CardTitle>
        </CardHeader>
        <CardBody className="!p-0">
          <ShrinkageMatrixTable rows={d?.matrix ?? []} loading={q.isLoading} />
        </CardBody>
      </Card>
    </div>
  );
}

function ShrinkageSkuTable({ rows, loading }: { rows: ShrinkageSkuRow[]; loading: boolean }) {
  if (loading) return <Skeleton className="m-4 h-40" />;
  if (rows.length === 0)
    return <EmptyState className="py-8" icon={<PackageX className="h-5 w-5" />} title="No SKUs" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <Th>Product</Th>
            <Th>Subcategory</Th>
            <Th className="text-right">Value</Th>
            <Th className="text-right">Units</Th>
            <Th className="text-right">Subcat mean</Th>
            <Th>Flag</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.product_id}`} className="border-b border-border/60">
              <Td>{r.product_name ?? `#${r.product_id}`}</Td>
              <Td className="text-muted-foreground">{r.subcategory_name}</Td>
              <Td className="text-right">{fmt.npr(r.value)}</Td>
              <Td className="text-right">{fmt.number(r.units)}</Td>
              <Td className="text-right text-muted-foreground">{fmt.npr(r.subcategory_mean)}</Td>
              <Td>{r.is_outlier ? <Badge tone="danger">Outlier</Badge> : null}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShrinkageMatrixTable({
  rows,
  loading,
}: {
  rows: ShrinkageMatrixCell[];
  loading: boolean;
}) {
  if (loading) return <Skeleton className="m-4 h-40" />;
  if (rows.length === 0)
    return <EmptyState className="py-8" icon={<PackageX className="h-5 w-5" />} title="No data" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <Th>Store</Th>
            <Th>Subcategory</Th>
            <Th className="text-right">Value</Th>
            <Th className="text-right">Units</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.store_id}-${r.subcategory_id}-${i}`} className="border-b border-border/60">
              <Td>{r.store_name ?? `Store #${r.store_id}`}</Td>
              <Td className="text-muted-foreground">{r.subcategory_name}</Td>
              <Td className="text-right">{fmt.npr(r.value)}</Td>
              <Td className="text-right">{fmt.number(r.units)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variance
// ---------------------------------------------------------------------------

function VarianceTab({ filters }: TabProps) {
  const q = useShrinkageVariance(filters);
  const d = q.data;

  if (q.isError) return <ErrorState onRetry={() => void q.refetch()} />;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Persistent negative variance</CardTitle>
          <Badge tone="neutral">recurring loss across audits</Badge>
        </CardHeader>
        <CardBody className="!p-0">
          <PersistentTable rows={d?.persistent_negative ?? []} loading={q.isLoading} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent audits — count vs book</CardTitle>
        </CardHeader>
        <CardBody>
          {q.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (d?.audits ?? []).length === 0 ? (
            <EmptyState
              icon={<ScanLine className="h-5 w-5" />}
              title="No stock audits"
              description="No physical counts recorded in this window."
            />
          ) : (
            <div className="space-y-3">
              {(d?.audits ?? []).map((a) => (
                <div key={a.audit_id} className="rounded-md border border-border">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                    <div className="text-sm">
                      <span className="font-medium">
                        {a.store_name ?? `Store #${a.store_id}`}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        Audit #{a.audit_id} · {a.created_at ? a.created_at.slice(0, 10) : '—'} ·{' '}
                        {a.status}
                      </span>
                    </div>
                    <span
                      className={cn(
                        'kpi-num text-sm font-semibold',
                        a.net_variance_value < 0 ? 'text-danger' : 'text-success',
                      )}
                    >
                      {fmt.npr(a.net_variance_value)} ({a.net_variance_units})
                    </span>
                  </div>
                  {a.lines.length > 0 ? (
                    <div className="overflow-x-auto border-t border-border/60">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-muted-foreground">
                            <Th>Product</Th>
                            <Th className="text-right">Counted</Th>
                            <Th className="text-right">Book</Th>
                            <Th className="text-right">Variance</Th>
                            <Th className="text-right">Value</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.lines.slice(0, 25).map((l) => (
                            <tr key={l.product_id} className="border-t border-border/40">
                              <Td>{l.product_name ?? `#${l.product_id}`}</Td>
                              <Td className="text-right">{fmt.number(l.counted)}</Td>
                              <Td className="text-right">{fmt.number(l.book)}</Td>
                              <Td
                                className={cn(
                                  'text-right',
                                  l.variance < 0 ? 'text-danger' : 'text-success',
                                )}
                              >
                                {l.variance}
                              </Td>
                              <Td className="text-right">{fmt.npr(l.value)}</Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          {d?.note ? <p className="mt-3 text-xs text-muted-foreground">{d.note}</p> : null}
        </CardBody>
      </Card>
    </div>
  );
}

function PersistentTable({
  rows,
  loading,
}: {
  rows: ShrinkagePersistentRow[];
  loading: boolean;
}) {
  if (loading) return <Skeleton className="m-4 h-40" />;
  if (rows.length === 0)
    return (
      <EmptyState
        className="py-8"
        icon={<ScanLine className="h-5 w-5" />}
        title="No recurring variance"
      />
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <Th>Product</Th>
            <Th className="text-right">Net variance</Th>
            <Th className="text-right">Net value</Th>
            <Th className="text-right">Audits</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.product_id}-${r.store_id}`} className="border-b border-border/60">
              <Td>{r.product_name ?? `#${r.product_id}`}</Td>
              <Td className="text-right text-danger">{r.net_variance}</Td>
              <Td className="text-right text-danger">{fmt.npr(r.net_value)}</Td>
              <Td className="text-right">{r.audits}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

function AttributionTab({ filters }: TabProps) {
  const q = useShrinkageAttribution(filters);
  const d = q.data;

  if (q.isError) return <ErrorState onRetry={() => void q.refetch()} />;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <KPICard
          label="Approved refunds (context)"
          value={fmt.number(d?.refund_context.approved_refunds ?? 0)}
          loading={q.isLoading}
          hint="Approved CRM refunds in window, per store scope"
        />
        <KPICard
          label="Refund value (context)"
          value={fmt.npr(d?.refund_context.approved_refund_value ?? 0)}
          loading={q.isLoading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Staff & rider risk ranking</CardTitle>
          <Badge tone="neutral">heuristic — investigation signal only</Badge>
        </CardHeader>
        <CardBody className="!p-0">
          <AttributionTable rows={d?.users ?? []} loading={q.isLoading} />
        </CardBody>
      </Card>
      {d?.note ? <p className="px-1 text-xs text-muted-foreground">{d.note}</p> : null}
    </div>
  );
}

function AttributionTable({
  rows,
  loading,
}: {
  rows: ShrinkageAttributionUser[];
  loading: boolean;
}) {
  if (loading) return <Skeleton className="m-4 h-40" />;
  if (rows.length === 0)
    return (
      <EmptyState
        className="py-8"
        icon={<ShieldAlert className="h-5 w-5" />}
        title="No attributable loss"
        description="No lost-item or damage reports tied to a user in this window."
      />
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <Th>User</Th>
            <Th>Role</Th>
            <Th className="text-right">Risk</Th>
            <Th className="text-right">Lost reports</Th>
            <Th className="text-right">Lost value</Th>
            <Th className="text-right">Damage reports</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={`${u.user_id}`} className="border-b border-border/60">
              <Td>{u.name || u.username || `#${u.user_id}`}</Td>
              <Td className="text-muted-foreground capitalize">{u.role ?? '—'}</Td>
              <Td className="text-right">
                <span
                  className={cn(
                    'kpi-num font-semibold',
                    u.risk_score >= 66
                      ? 'text-danger'
                      : u.risk_score >= 33
                        ? 'text-warning-soft'
                        : 'text-muted-foreground',
                  )}
                >
                  {u.risk_score.toFixed(0)}
                </span>
              </Td>
              <Td className="text-right">
                {fmt.number(u.lost_count)} · {fmt.number(u.lost_units)}u
              </Td>
              <Td className="text-right">{fmt.npr(u.lost_value)}</Td>
              <Td className="text-right">{fmt.number(u.damage_count)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Anomalies
// ---------------------------------------------------------------------------

function AnomaliesTab({ filters }: TabProps) {
  const q = useShrinkageAnomalies(filters);
  const d = q.data;

  if (q.isError) return <ErrorState onRetry={() => void q.refetch()} />;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Detected anomalies</CardTitle>
        </CardHeader>
        <CardBody>
          {q.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (d?.anomalies ?? []).length === 0 ? (
            <EmptyState
              icon={<ShieldAlert className="h-5 w-5" />}
              title="No anomalies detected"
              description="Lost-item volume and day-close cash are within normal range."
            />
          ) : (
            <div className="space-y-2">
              {(d?.anomalies ?? []).map((a, i) => (
                <AnomalyRow key={i} anomaly={a} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lost / damaged unit snapshot</CardTitle>
          <Badge tone="neutral">current state</Badge>
        </CardHeader>
        <CardBody className="!p-0">
          {q.isLoading ? (
            <Skeleton className="m-4 h-32" />
          ) : (d?.unit_snapshot ?? []).length === 0 ? (
            <EmptyState className="py-8" icon={<PackageX className="h-5 w-5" />} title="No units" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <Th>Store</Th>
                    <Th className="text-right">Lost units</Th>
                    <Th className="text-right">Damaged units</Th>
                  </tr>
                </thead>
                <tbody>
                  {(d?.unit_snapshot ?? []).map((s) => (
                    <tr key={`${s.store_id}`} className="border-b border-border/60">
                      <Td>{s.store_name ?? `Store #${s.store_id}`}</Td>
                      <Td className="text-right text-danger">{fmt.number(s.lost_units)}</Td>
                      <Td className="text-right">{fmt.number(s.damaged_units)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function AnomalyRow({ anomaly }: { anomaly: ShrinkageAnomaly }) {
  const Icon = anomaly.severity === 'critical' ? ShieldAlert : AlertTriangle;
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        anomaly.severity === 'critical'
          ? 'border-danger/30 bg-danger/5'
          : anomaly.severity === 'warning'
            ? 'border-warning/30 bg-warning/5'
            : 'border-border bg-surface',
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          anomaly.severity === 'critical'
            ? 'text-danger'
            : anomaly.severity === 'warning'
              ? 'text-warning'
              : 'text-muted-foreground',
        )}
        aria-hidden
      />
      <div className="min-w-0">
        <p className="font-medium">{anomaly.message}</p>
        <p className="text-xs text-muted-foreground">
          {anomaly.type}
          {anomaly.ts ? ` · ${anomaly.ts.slice(0, 10)}` : ''}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table primitives
// ---------------------------------------------------------------------------

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-2 font-medium', className)}>{children}</th>;
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2', className)}>{children}</td>;
}
